import { Box, HStack, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { Check, Clock, User } from "@phosphor-icons/react";
import { ACCENT, HERO_BG } from "./heroBackground";

const GREEN = "#5A8A1A";

// Pill affordance for the time / who row — now sits inside the dark
// "Before you start" box, so it uses a translucent-white fill rather than the
// light-gray treatment it had when it sat on the page's light background.
function InfoPill({ icon, children }) {
  return (
    <HStack bg="whiteAlpha.100" border="1px solid" borderColor="whiteAlpha.300" borderRadius="full" px={4} py={2} gap={2}>
      {icon}
      <Text fontSize="body.s" fontWeight="600" color="white">
        {children}
      </Text>
    </HStack>
  );
}

// `youll_learn_content`/`youll_need_content` are newline-separated plain text —
// one line per list item, no rich text field.
function linesFrom(text) {
  return (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

// Dark circle marker matching the website's pricing-card checkmark treatment
// (dark-green fill, lime tick) — same marker for both columns.
function MarkerCircle() {
  return (
    <Box
      boxSize="24px"
      borderRadius="full"
      bg={`${GREEN}66`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Check size={12} color={ACCENT} weight="bold" />
    </Box>
  );
}

function ChecklistColumn({ title, lines }) {
  if (lines.length === 0) return null;
  return (
    <VStack align="stretch" gap={4}>
      <Text fontSize="12px" fontWeight="700" color="white" textTransform="uppercase" letterSpacing="wide">
        {title}
      </Text>
      <VStack align="stretch" gap={4}>
        {lines.map((line, index) => (
          <HStack key={index} align="start" gap={3}>
            <MarkerCircle />
            <Text fontSize="body.m" color="whiteAlpha.800" pt="2px">
              {line}
            </Text>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}

export default function HelparticleheadingSlice({ slice }) {
  const { time, who, youll_learn_content, youll_need_content } = slice.primary;
  const learnLines = linesFrom(youll_learn_content);
  const needLines = linesFrom(youll_need_content);

  const hasPills = time || who;
  const hasChecklists = learnLines.length > 0 || needLines.length > 0;

  return (
    <VStack align="stretch" gap={4}>
      {(hasPills || hasChecklists) && (
        <Box bg={HERO_BG} border="1px solid" borderColor={ACCENT} borderRadius="xl" overflow="hidden">
          {hasPills && (
            <HStack gap={3} flexWrap="wrap" px={8} py={5} borderBottom={hasChecklists ? "1px solid" : "none"} borderColor="whiteAlpha.200">
              {time && <InfoPill icon={<Clock size={16} color="white" />}>{time}</InfoPill>}
              {who && <InfoPill icon={<User size={16} color="white" />}>{who}</InfoPill>}
            </HStack>
          )}
          {hasChecklists && (
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={0} px={8} py={8}>
              <Box pr={{ base: 0, md: 8 }}>
                <ChecklistColumn title="You'll learn" lines={learnLines} />
              </Box>
              <Box pl={{ base: 0, md: 8 }} mt={{ base: 8, md: 0 }}>
                <ChecklistColumn title="You'll need" lines={needLines} />
              </Box>
            </SimpleGrid>
          )}
        </Box>
      )}
    </VStack>
  );
}
